import { getScript } from '@/lib/utils';

const state = { reviewLocale: 'ko', brandLocale: null };

function normalizeLocale(locale) {
  switch (locale) {
    case 'en-US':
    case 'ja-JP':
    case 'ko-KR':
      return locale.substr(0, 2);
    default:
      return locale;
  }
}

function bindGoogleStyleRemover() {
  window.addEventListener('load', () => {
    document.querySelector('body').style = null;
  });
}

function isGoogleScriptLoaded() {
  return document.querySelector('#crema-google-translate')?.children?.length;
}

const getters = {
  translatable({ brandLocale, reviewLocale }) {
    return brandLocale && reviewLocale !== brandLocale;
  }
};

const actions = {
  initialize: ({
    dispatch,
    getters,
    state,
    rootState: {
      translation: { locale },
      reviewSettings: { reviewSettings }
    }
  }) => {
    state.brandLocale = normalizeLocale(locale);
    if (!getters.translatable) return;
    if (!reviewSettings.use_google_translation) return;

    dispatch(
      'translationManager/call',
      { method: 'initialize' },
      { root: true }
    );

    bindGoogleStyleRemover();
    dispatch('addNoTranslateClass');

    document.addEventListener('history:updated', () => {
      dispatch('addNoTranslateClass');
      dispatch('initTranslationStatus');
    });
  },
  initTranslationStatus({
    dispatch,
    rootState: {
      reviewSettings: {
        reviewSettings: {
          use_translate_button,
          default_translate_button_status
        }
      }
    }
  }) {
    dispatch(
      'translationManager/call',
      {
        method: 'initTranslationStatus',
        args: use_translate_button ? default_translate_button_status : 'on'
      },
      { root: true }
    );
  },
  storeTranslationStatus({ dispatch }, translationStatus) {
    dispatch(
      'translationManager/call',
      {
        method: 'changeTranslationStatus',
        args: translationStatus
      },
      { root: true }
    );
  },
  inheritTranslationStatus({ dispatch }, status) {
    if (status === 'on') dispatch('runTranslation');
    else if (status === 'off') dispatch('restoreTranslation');
  },
  hideTranslationIframe() {
    if (document.querySelector('#google_translation_button_remover')) return;

    const styleElement = document.createElement('style');
    styleElement.setAttribute('id', 'google_translation_button_remover');
    styleElement.append(
      'body {top: 0 !important;} .skiptranslate {display: none !important;}'
    );
    document.body.append(styleElement);
  },
  triggerClickGoogleButton({ dispatch }, action) {
    const iframe = document.querySelector('body .skiptranslate iframe');
    if (!iframe) return;

    const iframeContentDocument =
      iframe.contentDocument || iframe.contentWindow.document;
    const event = new Event('click');
    iframeContentDocument.querySelectorAll('button').forEach(button => {
      if (button.id.indexOf(action) >= 0) button.dispatchEvent(event);
    });
    dispatch('hideTranslationIframe');
  },
  selectTranslateLanguage({ dispatch }, counter) {
    const iframe = document.querySelector(
      'body iframe.skiptranslate:not([name="votingFrame"])'
    );
    if (!iframe) return;

    const iframeContentDocument =
      iframe.contentDocument || iframe.contentWindow.document;
    const languageLink = iframeContentDocument.querySelector(
      'body table tbody a:last-child'
    );
    if (languageLink) {
      languageLink.dispatchEvent(new Event('click'));
    } else if (counter >= 0) {
      setTimeout(
        () => dispatch('checkGoogleTranslateIframe', counter - 1),
        500
      );
    }
  },
  checkGoogleTranslateIframe({ dispatch }, counter) {
    const iframe = document.querySelector(
      'body .skiptranslate iframe:not([name="votingFrame"])'
    );
    if (iframe) {
      setTimeout(() => dispatch('triggerClickGoogleButton', 'confirm'), 500);
    } else {
      dispatch('selectTranslateLanguage', counter);
    }
  },
  initCallback({ dispatch, state }) {
    const checkGoogleIframeCounter = 5;
    if (isGoogleScriptLoaded()) {
      dispatch('checkGoogleTranslateIframe', checkGoogleIframeCounter);
      return;
    }

    window.translation_callback = () =>
      Promise.resolve(
        // eslint-disable-next-line no-undef
        new google.translate.TranslateElement(
          {
            pageLanguage: state.reviewLocale,
            includedLanguages: state.brandLocale,
            multilanguagePage: true,
            // eslint-disable-next-line no-undef
            layout: google.translate.TranslateElement.InlineLayout.SIMPLE
          },
          'crema-google-translate'
        )
      ).then(() => {
        setTimeout(
          () =>
            dispatch('checkGoogleTranslateIframe', checkGoogleIframeCounter),
          500
        );
      });
    const body = document.querySelector('body');
    if (body.style) {
      body.style = null;
    }
    getScript(
      '//translate.google.com/translate_a/element.js?cb=translation_callback'
    );
    dispatch('hideTranslationIframe');
  },
  addNoTranslateClass() {
    const contentElement = document.getElementById('app');
    if (!contentElement) return;

    const nodeIterator = document.createNodeIterator(
      contentElement,
      NodeFilter.SHOW_TEXT,
      node => {
        const trimmedText = node.data.trim();
        if (trimmedText) return NodeFilter.FILTER_ACCEPT;
      },
      false
    );
    let node;
    while ((node = nodeIterator.nextNode())) {
      const parentNode = node.parentNode;
      if (!parentNode.closest('.js-translate-text')) {
        parentNode.classList.add('notranslate');
      }
    }
  },
  runTranslation({ dispatch }) {
    dispatch('initCallback');
    const button = document.querySelector('.js-translation-button');
    if (!button) return;

    const buttonClassList = button.classList;
    if (
      !buttonClassList.contains('reviews_manager__button--on') &&
      !buttonClassList.contains('toggle_button_component')
    ) {
      buttonClassList.add('reviews_manager__button--on');
    }
    if (
      !buttonClassList.contains('active') &&
      buttonClassList.contains('toggle_button_component')
    ) {
      buttonClassList.add('active');
    }
  },
  restoreTranslation({ dispatch }) {
    const button = document.querySelector('.js-translation-button');
    button.classList.remove('active');
    dispatch('triggerClickGoogleButton', 'restore');
  }
};

export default { namespaced: true, state, getters, actions };
